import * as React from "react"
import { useRef, useState, useLayoutEffect } from "react"
import { Button } from "./button"
import { cn } from "@/lib/utils"
import { CheckIcon } from "@heroicons/react/24/outline"
import { Tooltip, TooltipTrigger, TooltipContent } from "./tooltip"

interface ToggleOption<T extends string> {
  value: T
  label: string
  icon?: React.ReactNode
  number?: number
  /** Attention count shown as a red pill after the label (same style as the sidebar unread count). */
  alert?: number
  completed?: boolean
  disabled?: boolean
  tooltip?: string
}

interface ToggleProps<T extends string> {
  options: ToggleOption<T>[]
  value: T
  onChange: (value: T) => void
  className?: string
  size?: "sm" | "default" | "lg"
  activeClassName?: string
  activeTextClassName?: string
}

function Toggle<T extends string>({
  options,
  value,
  onChange,
  className,
  activeClassName,
  activeTextClassName,
}: ToggleProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRefs = useRef<Map<T, HTMLButtonElement>>(new Map())
  const [sliderStyle, setSliderStyle] = useState<{ left: number; width: number } | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const prevValueRef = useRef<T>(value)

  // Update slider position when value changes
  useLayoutEffect(() => {
    const updateSliderPosition = () => {
      const activeButton = buttonRefs.current.get(value)
      const container = containerRef.current
      
      if (activeButton && container) {
        const containerRect = container.getBoundingClientRect()
        const buttonRect = activeButton.getBoundingClientRect()
        
        setSliderStyle({
          left: buttonRect.left - containerRect.left,
          width: buttonRect.width,
        })
        
        // Mark as initialized after first measurement
        if (!isInitialized) {
          setTimeout(() => setIsInitialized(true), 50)
        }
      }
    }

    // Trigger scale animation when value changes (not on initial render)
    if (isInitialized && prevValueRef.current !== value) {
      setIsAnimating(true)
      // Reset animation state after the animation completes
      setTimeout(() => setIsAnimating(false), 200)
    }
    prevValueRef.current = value

    updateSliderPosition()
    
    // Also update on resize
    window.addEventListener('resize', updateSliderPosition)
    return () => window.removeEventListener('resize', updateSliderPosition)
  }, [value, options, isInitialized])

  return (
    <div 
      ref={containerRef}
      className={cn(
        "relative flex bg-input-bg control-gradient rounded-full p-0.5 shadow-xl outline-1 outline-control-border",
        className
      )}
    >
      {/* Sliding background indicator */}
      {sliderStyle && (
        <div 
          className={cn(
            "absolute top-0.5 bottom-0.5 rounded-full z-0",
            activeClassName || "bg-toggle-active",
            isInitialized ? "transition-all duration-200 ease-out" : ""
          )}
          style={{
            left: sliderStyle.left,
            width: sliderStyle.width,
            transform: isAnimating ? 'scale(0.95)' : 'scale(1)',
          }}
        />
      )}
      
      {options.map((option) => {
        const button = (
          <Button
            ref={(el) => {
              if (el) buttonRefs.current.set(option.value, el)
            }}
            variant="ghost"
            onClick={() => !option.disabled && onChange(option.value)}
            className={cn(
              "relative z-10 rounded-full h-11 transition-colors duration-200 hover:bg-transparent px-4 flex items-center gap-2",
              value === option.value
                ? (activeTextClassName || "!text-primary")
                : "",
              option.disabled && "opacity-50 cursor-not-allowed pointer-events-auto"
            )}
            style={option.disabled ? { pointerEvents: 'auto' } : undefined}
          >
            {option.icon}
            <span className={cn(option.disabled && "text-content-disabled")}>
              {option.label}
            </span>
            {/* Neutral count, trailing the label so it mirrors the alert pill's position. */}
            {option.number !== undefined && (
              <div className={cn(
                "w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium",
                option.completed
                  ? "bg-primary text-on-accent"
                  : value === option.value
                  ? "bg-transparent border border-primary text-primary"
                  : option.disabled
                  ? "bg-transparent border border-content-disabled text-content-disabled"
                  : "bg-transparent border border-content-strong text-content-strong"
              )}>
                {option.completed ? (
                  <CheckIcon className="h-2 w-2 text-on-primary" />
                ) : (
                  option.number
                )}
              </div>
            )}
            {option.alert !== undefined && option.alert > 0 && (
              <span className="h-5 min-w-5 px-1 bg-danger-solid text-on-accent text-xs rounded-full flex items-center justify-center font-medium">
                {option.alert > 99 ? '99+' : option.alert}
              </span>
            )}
          </Button>
        );

        if (option.disabled) {
          return (
            <Tooltip key={option.value}>
              <TooltipTrigger asChild>
                {button}
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={10}>
                Complete the sequence flow and add at least one lead to access the launch step
              </TooltipContent>
            </Tooltip>
          );
        }

        if (option.tooltip) {
          return (
            <Tooltip key={option.value} delayDuration={400}>
              <TooltipTrigger asChild>
                {button}
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={10}>
                {option.tooltip}
              </TooltipContent>
            </Tooltip>
          );
        }

        return <React.Fragment key={option.value}>{button}</React.Fragment>;
      })}
    </div>
  )
}

export { Toggle }
export type { ToggleOption, ToggleProps }

